/* ═══════════════════════════════════════════════════════
   Monmouth Cards — Collection Page Enhancements
   assets/monmouth-collection-enhance.js

   Two bundled behaviors (shipped together per team decision — see
   phase-4-newest-sort-and-per-page-prompt.md):

   a) Default-to-Newest redirect. On a bare collection URL (no sort_by,
      no tag-based sort active), redirect once to add
      sort_by=created-descending. Reads whether a tag-based sort is
      already active from #mc-product-grid's data-active-sort-tag
      attribute (set server-side by sections/monmouth-collection-
      filters.liquid's own _active_sort_tag) rather than re-deriving
      that path-parsing logic here — the server is the source of truth
      for it, this just reads the answer. Can't loop: the redirect
      target always has sort_by set, so the "no sort_by" check is false
      on the very next load. A developer can add
      <body data-mc-default-newest="off"> in layout/theme.liquid to
      disable this storewide (not added by this change, just respected
      if it's ever added).

   b) Products-per-page selector (24/48/72). A display-layer choice on
      top of the real, merchant-configured products_per_page setting
      (read from #mc-product-grid's data-per-page — never hardcoded).
      Selecting a size larger than what's rendered fetches the
      additional pages needed via the same Shopify Sections Rendering
      API pattern monmouth-collection.js's fetchAndReplace/
      fetchSectionHtml already use, but appends #mc-product-grid's
      fetched product cards into the live grid instead of doing a full
      section swap. Remembered site-wide in localStorage. Re-applied
      after every AJAX section swap (a MutationObserver on
      .mc-collection-section's direct children — the same node
      fetchAndReplace's swapSectionHtml() replaces the innerHTML of —
      not touched via monmouth-collection.js itself, which this file
      deliberately doesn't edit beyond what the prompt's item 3 already
      covers).
   ═══════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* Must match SECTION_ID in monmouth-collection.js — that constant is
     module-private there (not exposed on window.MC), so it's duplicated
     here rather than invented differently. */
  var SECTION_ID = 'monmouth-collection-filters';
  var STORAGE_KEY = 'mc-products-per-page';
  var PER_PAGE_SIZE_OPTIONS = [24, 48, 72];

  var state = {
    loadedPages: 1,
    realPerPage: 24,
    targetSize: null
  };

  /* ══════════════════════════════════════════════════════
     a) DEFAULT-TO-NEWEST REDIRECT
  ══════════════════════════════════════════════════════ */
  function maybeRedirectToNewestDefault() {
    if (document.body.dataset.mcDefaultNewest === 'off') return;

    var grid = document.getElementById('mc-product-grid');
    if (!grid) return; // no products rendered — nothing to sort

    var url = new URL(window.location.href);
    if (url.searchParams.has('sort_by')) return; // shopper (or a previous redirect) already set one

    var activeSortTag = grid.getAttribute('data-active-sort-tag') || '';
    if (activeSortTag !== '') return; // a tag-based sort (Featured/Best Selling/Ending Soon, or a legacy sort-newest tag link) is already active — don't override it

    url.searchParams.set('sort_by', 'created-descending');
    window.location.replace(url.toString());
  }

  /* ══════════════════════════════════════════════════════
     b) PRODUCTS-PER-PAGE SELECTOR
  ══════════════════════════════════════════════════════ */

  /* Same fetch-JSON-via-Sections-Rendering-API approach as
     monmouth-collection.js's fetchSectionHtml — duplicated in miniature
     here (rather than calling into that module) because this needs to
     read out just #mc-product-grid's fetched contents to APPEND, not
     swap the whole section the way fetchAndReplace does. */
  function fetchGridFragment(url) {
    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    var apiUrl = url + sep + 'sections=' + SECTION_ID;
    return fetch(apiUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var html = data[SECTION_ID];
        if (!html) throw new Error('Section not found in response');
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.getElementById('mc-product-grid');
      });
  }

  function pageUrlFor(pageNum) {
    var url = new URL(window.location.href);
    if (pageNum > 1) {
      url.searchParams.set('page', String(pageNum));
    } else {
      url.searchParams.delete('page');
    }
    return url.toString();
  }

  function appendPage(pageNum) {
    var liveGrid = document.getElementById('mc-product-grid');
    if (!liveGrid) return Promise.resolve(0);
    return fetchGridFragment(pageUrlFor(pageNum)).then(function (fetchedGrid) {
      if (!fetchedGrid) return 0;
      var cards = fetchedGrid.querySelectorAll('.mc-product-card');
      cards.forEach(function (card) {
        liveGrid.appendChild(document.importNode(card, true));
      });
      state.loadedPages = pageNum;
      return cards.length;
    });
  }

  function hideLoadMoreButton() {
    var btn = document.getElementById('mc-load-more-btn');
    if (btn) btn.remove();
    var pager = document.querySelector('.mc-pagination');
    if (pager) pager.style.display = '';
  }

  function showLoadMoreButton() {
    var pager = document.querySelector('.mc-pagination');
    if (pager) pager.style.display = 'none';

    var existing = document.getElementById('mc-load-more-btn');
    if (existing) existing.remove();

    var main = document.querySelector('.mc-collection-main');
    if (!main) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'mc-load-more-btn';
    btn.textContent = 'Load more';
    btn.addEventListener('click', function () {
      btn.disabled = true;
      btn.textContent = 'Loading…';
      appendPage(state.loadedPages + 1)
        .then(function (count) {
          btn.disabled = false;
          btn.textContent = 'Load more';
          if (count === 0) hideLoadMoreButton(); // ran out of pages
        })
        .catch(function (err) {
          console.warn('[MC] Load more failed:', err);
          btn.disabled = false;
          btn.textContent = 'Load more';
        });
    });
    main.appendChild(btn);
  }

  /* target = 24/48/72 from the control, or null to reset to default.
     persist = whether to write the choice to localStorage (true for a
     real user selection; false when just re-applying a remembered
     choice on load/after a swap, so re-applying doesn't re-write the
     same value pointlessly). */
  function applyTargetSize(target, persist) {
    var grid = document.getElementById('mc-product-grid');
    if (!grid) return;

    var realPerPage = parseInt(grid.getAttribute('data-per-page'), 10) || 24;
    state.realPerPage = realPerPage;

    var select = document.getElementById('mc-per-page-select');

    if (!target || target <= realPerPage) {
      var wasEnlarged = state.targetSize !== null;
      state.targetSize = null;
      state.loadedPages = 1;
      hideLoadMoreButton();
      if (select) select.value = String(PER_PAGE_SIZE_OPTIONS[0]);
      if (persist) localStorage.removeItem(STORAGE_KEY);
      // Trim any previously-appended cards beyond the real per-page count
      // back off — otherwise switching back to the default leaves stray
      // products in the grid even though the pager (which only ever
      // covers the first realPerPage of them) is showing again.
      if (wasEnlarged) {
        var cards = grid.querySelectorAll('.mc-product-card');
        for (var i = realPerPage; i < cards.length; i++) {
          cards[i].remove();
        }
      }
      return;
    }

    state.targetSize = target;
    if (select) select.value = String(target);
    if (persist) localStorage.setItem(STORAGE_KEY, String(target));

    var pagesNeeded = Math.ceil(target / realPerPage) - 1;
    state.loadedPages = 1;

    var chain = Promise.resolve();
    for (var p = 2; p <= pagesNeeded + 1; p++) {
      (function (pageNum) {
        chain = chain.then(function () { return appendPage(pageNum); });
      })(p);
    }
    chain
      .then(function () { showLoadMoreButton(); })
      .catch(function (err) { console.warn('[MC] Enlarge page size failed:', err); });
  }

  function initPerPageControl() {
    var select = document.getElementById('mc-per-page-select');
    if (!select || select._mcBound) return;
    select._mcBound = true;

    select.addEventListener('change', function () {
      var val = parseInt(this.value, 10);
      applyTargetSize(val, true);
    });

    var remembered = parseInt(localStorage.getItem(STORAGE_KEY), 10);
    if (remembered) applyTargetSize(remembered, false);
  }

  /* Re-apply the per-page control's bound state (and any remembered
     enlarged size) after monmouth-collection.js's fetchAndReplace swaps
     .mc-collection-section's content for a filter/sort change — the
     freshly-swapped-in select is a brand new DOM node with no listener
     yet, and the freshly-swapped-in grid is back to the default
     per-page count regardless of what was showing before the swap. */
  function observeSectionSwaps() {
    var section = document.querySelector('.mc-collection-section');
    if (!section || !window.MutationObserver) return;
    var mo = new MutationObserver(function () {
      initPerPageControl();
    });
    mo.observe(section, { childList: true });
  }

  maybeRedirectToNewestDefault();
  initPerPageControl();
  observeSectionSwaps();
})();
